import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { LocalStorageService } from 'ngx-webstorage';
import * as lodash from 'lodash';
import { interval } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, skip, tap } from 'rxjs/operators';

import { slideInAnimation } from 'app/shared/animations';
import { SYSTEM_MENU } from 'app/shared/constants/common.constants';
import { SubscriptionBase } from 'app/base';
import { AccountService, LoginService, NotificationService, OrganizationService } from 'app/shared';
import { LayoutService } from '../layout.service';



@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  animations: [
    slideInAnimation
  ],
  host: {
    'class': 'd-flex flex-column flex-shrink-0 flex-grow-0 w-100 vh-100'
  }
})
export class MainComponent extends SubscriptionBase implements OnInit {
  get animationState(): string {
    return this.getDeepPath(this.route.firstChild.snapshot) || 'void';
  }

  @ViewChild('drawer', {
    static: true
  }) sideNav: MatDrawer;

  menu: Array<App.MenuItem> = SYSTEM_MENU;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private localStorage: LocalStorageService,
    private notification: NotificationService,
    private layoutService: LayoutService,
    private loginService: LoginService,
    private accountService: AccountService,
    private organizationService: OrganizationService
  ) {
    super();
  }

  ngOnInit() {
    let startTime = this.localStorage.retrieve('refreshTokenStartTime') || Date.now();

    this.localStorage.store('refreshTokenStartTime', startTime);
    this.subscription.add(
      interval(60000)
        .pipe(
          filter((): boolean => {
            const acitveTime = this.localStorage.retrieve('sessionActiveTime') || 0;

            console.info(`Refresh token countdown: ${Math.floor((14400000 - (Date.now() - acitveTime)) / 60000)} minutes`);
            if (Date.now() - acitveTime < 14400000) { // 14400000
              return true;
            }
            this.loginService.logout().subscribe((): void => {
              this.router.navigate(['/auth', 'sign-in']);
            });
            return false;
          }),
          distinctUntilChanged(() => {
            return Date.now() - startTime <= 14100000; // 14100000
          }),
          skip(1),
          mergeMap(() => {
            startTime = Date.now();
            return this.loginService.login({
              refresh_token: this.localStorage.retrieve('token')?.refreshToken,
              grant_type: 'refresh_token'
            })
              .pipe(
                tap((): void => {
                  this.localStorage.store('refreshTokenStartTime', startTime);
                })
              );
          })
        )
        .subscribe()
    );

    this.subscription.add(
      this.layoutService.getSideNavState()
        .pipe(
          filter((): boolean => {
            return !!this.sideNav;
          })
        )
        .subscribe((collapsed): void => {
          if (collapsed) {
            this.sideNav.close();
          } else {
            this.sideNav.open();
          }
        })
    );

    this.subscription.add(
      this.accountService.account$
        .pipe(
          distinctUntilChanged((prev, curr) => {
            if (!prev || !curr) {
              return true;
            }
            return lodash.isEqual(prev.roles, curr.roles);
          }),
          skip(1),
          map((account) => {
            if (!account.roles.length) {
              return this.loginService.logout()
                .pipe(
                  map(() => {
                    this.router.navigate(['/auth', 'deactivated'], {
                      state: account
                    });
                    return false;
                  })
                )
                .subscribe();
            }
            this.organizationService.reset();
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dialog.closeAll();
    this.notification.dismiss();
  }

  private getDeepPath(route: ActivatedRouteSnapshot): string {
    return [route.url.toString(), route.firstChild ? this.getDeepPath(route.firstChild) : undefined]
      .filter((item): boolean => {
        return !!item;
      }).join('/');
  }
}
